import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Smartphone, ShieldCheck, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function MobileVerifyCard({ onVerified }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  const send = async () => {
    if (phone.trim().length < 8) { toast.error("Enter a valid mobile number"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/mobile/send-otp", { phone: phone.trim() });
      setSent(true); setCooldown(30);
      if (r.data?.sms_status === "ok") toast.success("Code sent", { description: "We've texted your code — also check your email." });
      else toast.success("Check your email for the code", { description: "We've emailed your one-time code (SMS is being set up)." });
    }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try { await api.post("/auth/mobile/verify", { code: code.trim() }); toast.success("Mobile verified"); onVerified?.(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-3" data-testid="mobile-verify-card">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-blue-800"><Smartphone className="h-4 w-4" /> Verify your mobile number</p>
      <p className="mb-2 text-[11px] text-slate-500">We'll send a one-time code (emailed now; SMS/WhatsApp once a provider is connected).</p>
      <div className="flex flex-wrap items-center gap-2">
        <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Mobile number" data-testid="mobile-phone-input" className="h-9 w-44 rounded-lg border border-slate-200 px-2 text-sm outline-none" />
        <button onClick={send} disabled={busy || cooldown > 0} data-testid="mobile-send-btn" className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-blue-300 disabled:opacity-50">{cooldown > 0 ? `Resend in ${cooldown}s` : (sent ? "Resend code" : "Send code")}</button>
      </div>
      {sent && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" data-testid="mobile-code-input" className="h-9 w-36 rounded-lg border border-slate-200 px-2 text-sm outline-none" />
          <button onClick={verify} disabled={busy || code.trim().length < 4} data-testid="mobile-verify-btn" className="rounded-full brand-gradient-bg px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">Verify</button>
        </div>
      )}
    </div>
  );
}

export default function VerifyMobile() {
  const [req, setReq] = useState(null);
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const safeNext = next && next.startsWith("/") ? next : null;
  const load = () => api.get("/auth/mobile/requirement").then((r) => setReq(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const onVerified = () => {
    if (safeNext) { toast.success("Mobile verified — taking you back to finish up…"); nav(safeNext); }
    else load();
  };
  return (
    <div className="mx-auto max-w-lg p-6" data-testid="verify-mobile-page">
      <h1 className="mb-1 text-2xl font-bold text-slate-800">Mobile verification</h1>
      <p className="mb-4 text-sm text-slate-500">{safeNext ? "Confirm your mobile number to continue with what you started." : "Confirm your mobile number with a one-time code."}</p>
      {req?.verified ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700" data-testid="mobile-verified-banner">
          <ShieldCheck className="mb-1 h-5 w-5" /> Your mobile number is verified{req.phone ? ` (${req.phone})` : ""}.
          {safeNext && <button onClick={() => nav(safeNext)} data-testid="mobile-verified-continue" className="mt-3 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-xs font-semibold text-white">Continue <ArrowRight className="h-3.5 w-3.5" /></button>}
        </div>
      ) : (
        <MobileVerifyCard onVerified={onVerified} />
      )}
    </div>
  );
}
